'use client';

import { c as _c } from "react/compiler-runtime";
import './child-bio-image.scss';
import { useTranslation } from 'react-i18next';
import { HtmlBlock } from '@sola/ark-core/atoms/html/html.tsx';
import { ChildImageVariantEnum, getChildImageUrlBase } from '@sola/ark-core/utils/child-image.ts';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import type { ChildAvailabilityItem, ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import loader from '@sola/shared-utils/ark-skippy/loader.ts';
import type { ImageSizeIntent } from '../../atoms/image/image.tsx';
import { Image } from '../../atoms/image/image.tsx';
import { useSacImageAltText } from '../../utils/alt-text.ts';
import placeholder from './placeholder.png';
type ChildBioImageProps = {
  availability: ChildAvailabilityItem;
  details: ChildDetails;
  aspectRatio?: string;
  aspectRatioSm?: string;
  aspectRatioMd?: string;
  aspectRatioLg?: string;
  childImageVariant?: ChildImageVariantEnum;
  caption?: string;
  centerCaption?: boolean;
  showCaption?: boolean;
  /**
   * Drives the `sizes` attribute. Defaults to `third`, matching the bio layout
   * where the media column is `flex: 1` against a `flex: 2` content column.
   */
  sizeIntent?: ImageSizeIntent;
  fullWidth?: boolean;
};
export const ChildBioImage = t0 => {
  const $ = _c(41);
  const {
    details,
    availability,
    aspectRatio: t1,
    aspectRatioSm,
    aspectRatioMd,
    aspectRatioLg,
    childImageVariant: t2,
    caption,
    centerCaption: t3,
    showCaption: t4,
    sizeIntent: t5,
    fullWidth: t6
  } = t0;
  const aspectRatio = t1 === undefined ? "3-4" : t1;
  const childImageVariant = t2 === undefined ? ChildImageVariantEnum.Full : t2;
  const centerCaption = t3 === undefined ? false : t3;
  const showCaption = t4 === undefined ? true : t4;
  const sizeIntent = t5 === undefined ? "third" : t5;
  const fullWidth = t6 === undefined ? false : t6;
  let t7;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["bio", "children", "geo"];
    $[0] = t7;
  } else {
    t7 = $[0];
  }
  const {
    t
  } = useTranslation(t7);
  const {
    imageUrl,
    name,
    hangul
  } = details;
  const {
    gender,
    isSpecialNeeds,
    isOrphan,
    isInHivAffectedArea,
    minDaysWaiting,
    icp: t8
  } = availability;
  const {
    country
  } = t8;
  let t9;
  if ($[1] !== childImageVariant || $[2] !== imageUrl) {
    t9 = getChildImageUrlBase(imageUrl, childImageVariant);
    $[1] = childImageVariant;
    $[2] = imageUrl;
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const childImage = t9;
  const titleAndAlt = useSacImageAltText(gender, country, hangul, minDaysWaiting, name.preferred, isOrphan, isSpecialNeeds, isInHivAffectedArea);
  let t10;
  if ($[4] !== hangul.preferred || $[5] !== minDaysWaiting || $[6] !== name.preferred || $[7] !== t) {
    t10 = t("bio:waitingCaption_short", {
      childName: name.preferred,
      koreanName: hangul.preferred,
      days: minDaysWaiting
    });
    $[4] = hangul.preferred;
    $[5] = minDaysWaiting;
    $[6] = name.preferred;
    $[7] = t;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const captionShort = t10;
  let t11;
  if ($[9] !== hangul.preferred || $[10] !== minDaysWaiting || $[11] !== name.preferred || $[12] !== t) {
    t11 = t("bio:waitingCaption", {
      childName: name.preferred,
      koreanName: hangul.preferred,
      days: minDaysWaiting
    });
    $[9] = hangul.preferred;
    $[10] = minDaysWaiting;
    $[11] = name.preferred;
    $[12] = t;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const captionFull = t11;
  const captionText = caption ?? (fullWidth ? captionShort : captionFull);
  let t12;
  if ($[14] !== fullWidth) {
    t12 = cn("ChildBioImage", {
      "ChildBioImage--fullWidth": fullWidth
    });
    $[14] = fullWidth;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const t13 = childImage || placeholder;
  let t14;
  if ($[16] !== t13 || $[17] !== titleAndAlt) {
    t14 = {
      url: t13,
      alt: titleAndAlt
    };
    $[16] = t13;
    $[17] = titleAndAlt;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== aspectRatio || $[20] !== aspectRatioLg || $[21] !== aspectRatioMd || $[22] !== aspectRatioSm || $[23] !== childImage || $[24] !== sizeIntent || $[25] !== t14) {
    t15 = <Image key={childImage} image={t14} loader={loader} sizeIntent={sizeIntent} lazyLoad="eager" aspectRatio={aspectRatio} aspectRatioSm={aspectRatioSm} aspectRatioMd={aspectRatioMd} aspectRatioLg={aspectRatioLg} className="ChildBioImage__Figure__img" />;
    $[19] = aspectRatio;
    $[20] = aspectRatioLg;
    $[21] = aspectRatioMd;
    $[22] = aspectRatioSm;
    $[23] = childImage;
    $[24] = sizeIntent;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== caption || $[28] !== captionFull || $[29] !== captionShort || $[30] !== captionText || $[31] !== centerCaption || $[32] !== fullWidth || $[33] !== showCaption) {
    t16 = showCaption && <figcaption className={cn("ChildBioImage__Figure__figcaption label label-xl", {
      "text-center": centerCaption,
      "text-center sm:text-left": fullWidth
    })}>{fullWidth && !caption ? <><div className="sm:hidden"><HtmlBlock htmlString={captionShort} /></div><div className="hidden sm:block"><HtmlBlock htmlString={captionFull} /></div></> : <HtmlBlock htmlString={captionText} />}</figcaption>;
    $[27] = caption;
    $[28] = captionFull;
    $[29] = captionShort;
    $[30] = captionText;
    $[31] = centerCaption;
    $[32] = fullWidth;
    $[33] = showCaption;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <figure className="ChildBioImage__Figure">{t15}{t16}</figure>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== t12 || $[39] !== t17) {
    t18 = <div className={t12}>{t17}</div>;
    $[38] = t12;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
};